import React, { useState } from "react";
import { useMutation, QueryKey, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { solicitudes } from "api/solicitudes";
import {
  SolicitudConsumoAnalisisResponse,
  DesistirSolicitudConsumoRequest,
  AnularLiquidacionConsumoRequest,
  AprobarSolicitudConsumoRequest,
} from "api/types/originacion/services/analisisSolicitudTypes";

import { EEstadoSolicitudConsumo } from "api/types/originacion/businessTypes";

import useToken from "components/hooks/useToken";

import DesistirSolicitud from "./actions/DesistirSolicitud";
import AprobarSolicitud from "./actions/AprobarSolicitud";
import AnularLiquidacion from "./actions/AnularLiquidacion";
import VerOperacionEuro from "./actions/VerOperacionEuro";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useAuth } from "components/app/auth/useAuth";
import { toast } from "react-toastify";

interface ISolicitudAnalisisActions {
  solicitud: SolicitudConsumoAnalisisResponse;
}

const SolicitudRevisionActions = ({ solicitud }: ISolicitudAnalisisActions) => {
  const location = useLocation();
  const {
    desistirCredito,
    anularLiquidacion,
    aprobarCredito,
    anularError,
    clearAnularError,
    isSuccessAnular,
    mutationLoading,
  } = useAnalisisActions();
  const { yenConsumoAprobacion, yenConsumoAnulacionLiquidacion } = useFlags();

  const canDesistirOrRechazar =
    solicitud.estado !== EEstadoSolicitudConsumo.Liquidada &&
    solicitud.estado !== EEstadoSolicitudConsumo.Rechazada &&
    solicitud.estado !== EEstadoSolicitudConsumo.Desistida &&
    yenConsumoAprobacion;

  const canAprobar =
    solicitud.estado === EEstadoSolicitudConsumo.En_Analisis &&
    yenConsumoAprobacion;

  const canAnularLiquidacion =
    solicitud.estado === EEstadoSolicitudConsumo.Liquidada &&
    yenConsumoAnulacionLiquidacion;

  const isSolicitudesView = location.pathname.startsWith("/solicitudes");
  const isAnalisisView = location.pathname.startsWith("/revision");
  const isObOnlineView = location.pathname.startsWith("/ob-online");

  const showAprobarButton = canAprobar && (isObOnlineView || isAnalisisView);

  const showAnularLiquidacionButton = canAnularLiquidacion && isSolicitudesView;

  const showVerEnEuroButton =
    solicitud.estado === EEstadoSolicitudConsumo.Liquidada && isSolicitudesView;

  const handleDesistir = (comentario: string) => {
    desistirCredito({ comentario, id: solicitud.id });
  };

  const handleAnularLiquidacion = (comentario: string) => {
    clearAnularError();
    anularLiquidacion({ comentario, id: solicitud.id });
  };

  const handleAprobar = (tipoCallback: number, estadoTicket: number) => {
    aprobarCredito({ tipoCallback, estadoTicket, id: solicitud.id });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
      {canDesistirOrRechazar && (
        <DesistirSolicitud
          handleDesistir={handleDesistir}
          isLoading={mutationLoading}
          solicitud={solicitud}
        />
      )}
      {showAnularLiquidacionButton && (
        <AnularLiquidacion
          handleAnularLiquidacion={handleAnularLiquidacion}
          isLoading={mutationLoading}
          solicitud={solicitud}
          error={anularError}
          isSuccess={isSuccessAnular}
          onClearError={clearAnularError}
        />
      )}
      {showVerEnEuroButton && (
        <VerOperacionEuro operacionId={solicitud.operacionId} />
      )}
      {showAprobarButton && (
        <AprobarSolicitud
          handleAprobar={handleAprobar}
          isLoading={mutationLoading}
          solicitud={solicitud}
        />
      )}
    </div>
  );
};
export default SolicitudRevisionActions;

const useAnalisisActions = () => {
  const token = useToken();
  const queryClient = useQueryClient();
  const [anularError, setAnularError] = useState<string | null>(null);
  const { session } = useAuth();

  const getErrorMessage = (error: any, fallback: string): string =>
    error?.responseStatus?.message || fallback;

  const desistirPrestamoRequest = async (
    request: DesistirSolicitudConsumoRequest,
  ) => {
    if (!token) throw new Error("Must be authenticated");
    await solicitudes(token).desistirConsumo(request);
  };

  const { mutate: desistirCredito, isLoading: isLoadingDesistir } = useMutation(
    desistirPrestamoRequest,
    {
      onMutate: ({ id }) => {
        const cacheKey = ["detalleSolicitudConsumo", { id }] as QueryKey;
        const snapshot = queryClient.getQueryData(cacheKey);
        updateCacheSolicitud(id, EEstadoSolicitudConsumo.Desistida);
        return { snapshot, cacheKey };
      },
      onSuccess: (_, { id }) => {
        invalidateCacheSolicitud(id);
        toast.success("Solicitud desistida exitosamente");
      },
      onError: (error: any, { id }, context: any) => {
        if (context?.snapshot) {
          queryClient.setQueryData(context.cacheKey, context.snapshot);
        }
        toast.error(getErrorMessage(error, "Error al desistir la solicitud"));
      },
    },
  );

  const anularLiquidacionRequest = async (
    request: AnularLiquidacionConsumoRequest,
  ) => {
    if (!token) throw new Error("Must be authenticated");
    const requestWithUserData = {
      ...request,
      fecha: new Date(),
      usuario: session?.user?.euroUserId,
    };
    await solicitudes(token).anularLiquidacionConsumo(requestWithUserData);
  };

  const {
    mutate: anularLiquidacion,
    isLoading: isLoadingAnular,
    isSuccess: isSuccessAnular,
  } = useMutation(anularLiquidacionRequest, {
    onMutate: () => {
      setAnularError(null);
    },
    onSuccess: (_, { id }) => {
      setAnularError(null);
      invalidateCacheSolicitud(id);
      toast.success("Liquidación anulada exitosamente");
    },
    onError: (error: any) => {
      const message = getErrorMessage(error, "Error al anular la liquidación");
      setAnularError(message);
      toast.error(message);
    },
  });

  const aprobarCreditoRequest = async (
    request: AprobarSolicitudConsumoRequest,
  ) => {
    if (!token) throw new Error("Must be authenticated");
    await solicitudes(token).aprobarConsumo(request);
  };

  const { mutate: aprobarCredito, isLoading: isLoadingAprobar } = useMutation(
    aprobarCreditoRequest,
    {
      onMutate: ({ id }) => {
        const cacheKey = ["detalleSolicitudConsumo", { id }] as QueryKey;
        const snapshot = queryClient.getQueryData(cacheKey);
        updateCacheSolicitud(id, EEstadoSolicitudConsumo.Aprobada);
        return { snapshot, cacheKey };
      },
      onSuccess: (_, { id }) => {
        invalidateCacheSolicitud(id);
        toast.success("Solicitud aprobada exitosamente");
      },
      onError: (error: any, _vars, context: any) => {
        if (context?.snapshot) {
          queryClient.setQueryData(context.cacheKey, context.snapshot);
        }
        toast.error(getErrorMessage(error, "Error al aprobar la solicitud"));
      },
    },
  );

  const updateCacheSolicitud = (
    id: number,
    estado: EEstadoSolicitudConsumo,
  ) => {
    const cacheKey = ["detalleSolicitudConsumo", { id }] as QueryKey;
    const prevSolicitud = queryClient.getQueryData(
      cacheKey,
    ) as SolicitudConsumoAnalisisResponse;
    queryClient.setQueryData(cacheKey, { ...prevSolicitud, estado });
  };

  const invalidateCacheSolicitud = (id: number) => {
    const cacheKey = ["detalleSolicitudConsumo", { id }] as QueryKey;
    queryClient.invalidateQueries(cacheKey);
  };

  return {
    desistirCredito,
    anularLiquidacion,
    aprobarCredito,
    anularError,
    isSuccessAnular,
    clearAnularError: () => setAnularError(null),
    mutationLoading: isLoadingDesistir || isLoadingAprobar || isLoadingAnular,
  };
};
